import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { apiClient } from '@/lib/api'
import { NotificationToggleItem } from './NotificationToggleItem'
import {
	MessageSquare,
	MessageCircle,
	Mail,
	Bug,
	Lightbulb,
	Star,
	Info,
	Building2,
	AtSign,
} from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { AlertTriangle } from 'lucide-react'
import { useTranslation } from '@/hooks/useTranslation'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@/components/ui/accordion'

interface NotificationPreferences {
	notify_on_survey_response: boolean
	notify_on_review: boolean
	notify_on_bug_report: boolean
	notify_on_feature_request: boolean
	notify_on_contact_message: boolean
	notify_on_chat_message: boolean
	notify_on_brand_mentions: boolean
}

interface ProgramWithPreferences {
	id: string
	name: string
	team_id: string
	team_name: string
	role: string
	preferences: NotificationPreferences
}

interface UserProgramPreferencesResponse {
	programs: ProgramWithPreferences[]
}

const NotificationSettings = () => {
	const { t } = useTranslation()
	const { toast } = useToast()
	const queryClient = useQueryClient()

	const {
		data: programsData,
		isLoading,
		error,
	} = useQuery<UserProgramPreferencesResponse, Error>({
		queryKey: ['userProgramNotificationPreferences'],
		queryFn: async () => {
			return apiClient<UserProgramPreferencesResponse>(
				'/api/user-notification-preferences'
			)
		},
	})

	const updateMutation = useMutation<
		{ success: boolean; preferences: NotificationPreferences },
		Error,
		{ programId: string; preferences: Partial<NotificationPreferences> }
	>({
		mutationFn: async ({ programId, preferences }) => {
			return apiClient<{ success: boolean; preferences: NotificationPreferences }>(
				`/api/user-notification-preferences/${programId}`,
				{
					method: 'PUT',
					data: preferences,
				}
			)
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ['userProgramNotificationPreferences'] })
			
			const program = programsData?.programs.find(p => p.id === variables.programId)
			toast({
				title: t('notificationSettings.settingsUpdated') || 'Settings Updated',
				description: t('notificationSettings.notificationPreferencesSaved', {
					programName: program?.name || 'this program'
				}) || `Your notification preferences for ${program?.name || 'this program'} have been saved.`,
			})
		},
		onError: (error) => {
			toast({
				variant: 'destructive',
				title: t('notificationSettings.updateFailed') || 'Update Failed',
				description:
					error.message ||
					t('notificationSettings.couldNotSaveNotificationPreferences') || 'Could not save your notification preferences',
			})
		},
	})

	const handleSettingChange = (
		programId: string,
		key: keyof NotificationPreferences,
		value: boolean
	) => {
		updateMutation.mutate({
			programId,
			preferences: { [key]: value },
		})
	}

	if (isLoading) {
		return (
			<div className="space-y-4">
				{[...Array(3)].map((_, i) => (
					<div key={i} className="rounded-lg border p-4 space-y-3">
						<Skeleton className="h-6 w-1/3" />
						<Skeleton className="h-20 w-full" />
					</div>
				))}
			</div>
		)
	}

	if (error) {
		return (
			<Alert variant="destructive">
				<AlertTriangle className="h-4 w-4" />
				<AlertTitle>{t('notificationSettings.errorLoadingSettings') || 'Error Loading Settings'}</AlertTitle>
				<AlertDescription>
					{t('notificationSettings.couldNotLoadNotificationSettings') || 'Could not load notification settings. Please try again later.'}
				</AlertDescription>
			</Alert>
		)
	}

	if (!programsData || programsData.programs.length === 0) {
		return (
			<Alert
				variant="default"
				className="bg-blue-50 border-blue-200 dark:bg-blue-900/30 dark:border-blue-700">
				<Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
				<AlertTitle className="text-blue-800 dark:text-blue-300">
					{t('notificationSettings.noPrograms') || 'No Programs'}
				</AlertTitle>
				<AlertDescription className="text-blue-700 dark:text-blue-400">
					{t('notificationSettings.noProgramsDescription') || 'You are not currently part of any programs. Join or create a program to configure notification settings.'}
				</AlertDescription>
			</Alert>
		)
	}

	return (
		<div className="space-y-6">
			<div>
				<h2 className="text-2xl font-semibold mb-2">
					{t('notificationSettings.title') || 'Notification Settings'}
				</h2>
				<p className="text-sm text-muted-foreground">
					{t('notificationSettings.description') || 'Configure your personal email notification preferences for each program. These settings are personal to you and independent of your role.'}
				</p>
			</div>

			<Accordion type="multiple" className="w-full space-y-4">
				{programsData.programs.map((program) => (
					<AccordionItem
						key={program.id}
						value={program.id}
						className="border rounded-lg px-4 data-[state=open]:bg-muted/30">
						<AccordionTrigger className="hover:no-underline py-4">
							<div className="flex items-center gap-3 text-left">
								<Building2 className="h-5 w-5 text-primary shrink-0" />
								<div>
									<div className="font-medium">{program.name}</div>
									<div className="text-sm text-muted-foreground">
										{program.team_name} • {program.role}
									</div>
								</div>
							</div>
						</AccordionTrigger>
						<AccordionContent className="pb-4 pt-2">
							<div className="space-y-4 pl-8">
								<NotificationToggleItem
									icon={MessageSquare}
									title={t('notificationSettings.newSurveyResponses') || 'New Survey Responses'}
									description={t('notificationSettings.newSurveyResponsesDescription') || 'Get notified when someone submits a survey response'}
									isChecked={program.preferences.notify_on_survey_response}
									onCheckedChange={(checked) =>
										handleSettingChange(program.id, 'notify_on_survey_response', checked)
									}
									isLoading={
										updateMutation.isPending &&
										updateMutation.variables?.programId === program.id &&
										updateMutation.variables?.preferences?.notify_on_survey_response !== undefined
									}
								/>
								<NotificationToggleItem
									icon={Star}
									title={t('notificationSettings.newReviewsTestimonials') || 'New Reviews/Testimonials'}
									description={t('notificationSettings.newReviewsTestimonialsDescription') || 'Get notified when someone submits a review'}
									isChecked={program.preferences.notify_on_review}
									onCheckedChange={(checked) =>
										handleSettingChange(program.id, 'notify_on_review', checked)
									}
									isLoading={
										updateMutation.isPending &&
										updateMutation.variables?.programId === program.id &&
										updateMutation.variables?.preferences?.notify_on_review !== undefined
									}
								/>
								<NotificationToggleItem
									icon={Bug}
									title={t('notificationSettings.newBugReports') || 'New Bug Reports'}
									description={t('notificationSettings.newBugReportsDescription') || 'Get notified when someone reports a bug'}
									isChecked={program.preferences.notify_on_bug_report}
									onCheckedChange={(checked) =>
										handleSettingChange(program.id, 'notify_on_bug_report', checked)
									}
									isLoading={
										updateMutation.isPending &&
										updateMutation.variables?.programId === program.id &&
										updateMutation.variables?.preferences?.notify_on_bug_report !== undefined
									}
								/>
								<NotificationToggleItem
									icon={MessageCircle}
									title={t('notificationSettings.newLiveChatMessages') || 'New Live Chat Messages'}
									description={t('notificationSettings.newLiveChatMessagesDescription') || 'Get notified when a visitor sends a live chat message'}
									isChecked={program.preferences.notify_on_chat_message}
									onCheckedChange={(checked) =>
										handleSettingChange(program.id, 'notify_on_chat_message', checked)
									}
									isLoading={
										updateMutation.isPending &&
										updateMutation.variables?.programId === program.id &&
										updateMutation.variables?.preferences?.notify_on_chat_message !== undefined
									}
								/>
								<NotificationToggleItem
									icon={Mail}
									title={t('notificationSettings.newMailboxMessages') || 'New Mailbox Messages'}
									description={t('notificationSettings.newMailboxMessagesDescription') || 'Get notified when someone sends a contact message'}
									isChecked={program.preferences.notify_on_contact_message}
									onCheckedChange={(checked) =>
										handleSettingChange(program.id, 'notify_on_contact_message', checked)
									}
									isLoading={
										updateMutation.isPending &&
										updateMutation.variables?.programId === program.id &&
										updateMutation.variables?.preferences?.notify_on_contact_message !== undefined
									}
								/>
								<NotificationToggleItem
									icon={Lightbulb}
									title={t('notificationSettings.newFeatureRequests') || 'New Feature Requests'}
									description={t('notificationSettings.newFeatureRequestsDescription') || 'Get notified when someone submits a feature request'}
									isChecked={program.preferences.notify_on_feature_request}
									onCheckedChange={(checked) =>
										handleSettingChange(program.id, 'notify_on_feature_request', checked)
									}
									isLoading={
										updateMutation.isPending &&
										updateMutation.variables?.programId === program.id &&
										updateMutation.variables?.preferences?.notify_on_feature_request !== undefined
									}
								/>
								<NotificationToggleItem
									icon={AtSign}
									title={t('notificationSettings.newBrandMentions')}
									description={t('notificationSettings.newBrandMentionsDescription')}
									isChecked={program.preferences.notify_on_brand_mentions !== false}
									onCheckedChange={(checked) =>
										handleSettingChange(program.id, 'notify_on_brand_mentions', checked)
									}
									isLoading={
										updateMutation.isPending &&
										updateMutation.variables?.programId === program.id &&
										updateMutation.variables?.preferences?.notify_on_brand_mentions !== undefined
									}
								/>
							</div>
						</AccordionContent>
					</AccordionItem>
				))}
			</Accordion>
		</div>
	)
}

export default NotificationSettings
